import { Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

interface CronField {
  label: string;
  options: Array<{ value: string; label: string }>;
}

@Component({
  selector: 'app-cron-builder',
  templateUrl: './cron-builder.component.html',
  styleUrls: ['./cron-builder.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CronBuilderComponent),
      multi: true,
    },
  ],
})
export class CronBuilderComponent implements OnInit, OnDestroy, ControlValueAccessor {
  // When provided, the preview list renders these runs (computed by the backend)
  // instead of the local nextCronRuns pipe. Other usages (list/detail) keep their
  // local pipe behavior by leaving this null.
  @Input() externalRuns: Date[] | null = null;

  fg: FormGroup;
  destroyed$ = new Subject();

  readonly minuteOptions = this.buildOptions(60, null, [5, 10, 15, 30], 'min');
  readonly hourOptions = this.buildOptions(24, 'labels.every_hour', [2, 3, 4, 6, 8, 12], 'hour');
  readonly dayOfMonthOptions = this.buildDayOptions();
  readonly monthOptions = this.buildMonthOptions();
  readonly dayOfWeekOptions = this.buildDayOfWeekOptions();

  private onChange: (val: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.fg = this.fb.group({
      minute: ['0'],
      hour: ['9'],
      dayOfMonth: ['*'],
      month: ['*'],
      dayOfWeek: ['*'],
    });

    this.fg.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      const expr = this.buildExpression();
      this.onChange(expr);
      this.onTouched();
    });

    setTimeout(() => this.onChange(this.buildExpression()), 0);
  }

  buildExpression(): string {
    const v = this.fg.value;
    return `${v.minute} ${v.hour} ${v.dayOfMonth} ${v.month} ${v.dayOfWeek}`;
  }

  writeValue(val: string): void {
    if (!val) return;
    const parts = val.trim().split(/\s+/);
    if (parts.length === 5) {
      this.fg?.patchValue({
        minute: parts[0],
        hour: parts[1],
        dayOfMonth: parts[2],
        month: parts[3],
        dayOfWeek: parts[4],
      }, { emitEvent: false });
    }
  }

  registerOnChange(fn: (val: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }

  private buildOptions(
    count: number,
    everyLabel: string | null,
    steps: number[] = [],
    stepUnit = '',
  ): Array<{ value: string; label: string }> {
    const opts: Array<{ value: string; label: string }> = [];
    if (everyLabel) opts.push({ value: '*', label: everyLabel });
    steps.forEach(s =>
      opts.push({ value: `*/${s}`, label: `labels.cron_step.${stepUnit}_${s}` }),
    );
    for (let i = 0; i < count; i++) {
      opts.push({ value: String(i), label: String(i).padStart(2, '0') });
    }
    return opts;
  }

  private buildDayOptions(): Array<{ value: string; label: string }> {
    const opts: Array<{ value: string; label: string }> = [{ value: '*', label: 'labels.every_day' }];
    for (let i = 1; i <= 31; i++) {
      opts.push({ value: String(i), label: String(i) });
    }
    return opts;
  }

  private buildMonthOptions(): Array<{ value: string; label: string }> {
    const monthKeys = [
      'labels.months.jan', 'labels.months.feb', 'labels.months.mar', 'labels.months.apr',
      'labels.months.may', 'labels.months.jun', 'labels.months.jul', 'labels.months.aug',
      'labels.months.sep', 'labels.months.oct', 'labels.months.nov', 'labels.months.dec',
    ];
    const opts: Array<{ value: string; label: string }> = [{ value: '*', label: 'labels.every_month' }];
    monthKeys.forEach((k, i) => opts.push({ value: String(i + 1), label: k }));
    return opts;
  }

  private buildDayOfWeekOptions(): Array<{ value: string; label: string }> {
    const days = [
      'labels.days.sun', 'labels.days.mon', 'labels.days.tue', 'labels.days.wed',
      'labels.days.thu', 'labels.days.fri', 'labels.days.sat',
    ];
    const opts: Array<{ value: string; label: string }> = [{ value: '*', label: 'labels.every_weekday' }];
    days.forEach((d, i) => opts.push({ value: String(i), label: d }));
    return opts;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
